import React from "react";
import type { CSSProperties } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import "./Table.css";

type ColumnMetaAlign = NonNullable<CSSProperties["textAlign"]>;

function columnTextAlign(meta: unknown): ColumnMetaAlign {
  const raw = (meta as { align?: string } | undefined)?.align;
  if (raw === "center" || raw === "right" || raw === "end" || raw === "start") {
    return raw;
  }
  return "left";
}

export type TableType = {
  data: any[];
  columns: any[];
  sx?: any;
};

function Table({ data, columns, sx }: TableType) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid var(--mui-palette-primary-light)",
        paddingBottom: "24px",
        ...sx,
      }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
        }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <tr className="header-styling" key={headerGroup.id}>
              {headerGroup.headers.map((header: any) => {
                const headerAlign = columnTextAlign(header.column.columnDef.meta);
                const headerPad =
                  headerAlign === "center"
                    ? { paddingLeft: "16px", paddingRight: "16px" }
                    : { paddingLeft: "24px", paddingRight: "24px" };
                return (
                  <th
                    className="rounded-corners"
                    key={header.id}
                    style={{
                      width: `${header.column.columnDef.customSizing ? `${header.column.columnDef.customSizing.size}%` : "auto"}`,
                    }}>
                    <Typography
                      component={"p"}
                      color={"primary.main"}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        ...headerPad,
                        textAlign: headerAlign,
                      }}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </Typography>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row: any) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell: any) => {
                const cellAlign = columnTextAlign(cell.column.columnDef.meta);
                const cellPad =
                  cellAlign === "center"
                    ? {
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                      }
                    : {
                        paddingTop: "5px",
                        paddingBottom: "5px",
                        paddingLeft: "24px",
                      };
                return (
                  <td
                    style={{
                      ...cellPad,
                      textAlign: cellAlign,
                    }}
                    key={cell.id}>
                    <Box
                      sx={
                        cellAlign === "center"
                          ? {
                              display: "flex",
                              justifyContent: "center",
                              width: "100%",
                            }
                          : undefined
                      }>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Box>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

export default Table;
